import type { MaybeRef } from '@vueuse/core'
import type { Prompt, PromptMinified } from '../types/prompt'
import type { RecentItem, RecentItemActionEnum, User, UserCreate, UserInformation, UserProfile, UserUpdate } from '../types/user'
import type { APIIntervals, APIOrderings, APIPromptTypeId } from '../types/api'
import type { Elemental } from '../types/elemental'
import type { ElementalQuery } from './elementals'
import { APIFetch, filterResponse, filterResponseBoolean } from '.'

export interface GetUsersQuery {
  page?: number
  size?: number
  order_dir?: 'asc' | 'desc'
  order_by?: APIOrderings
  interval?: APIIntervals
  search?: string
}
export const getUsers = async (queries?: GetUsersQuery): Promise<null | User[]> => {
  let url = '/users'

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const createUser = async (userId: string, body: UserCreate): Promise<null | User> => {
  return filterResponse(await APIFetch<User>(`/users/${userId}`, {
    method: 'POST',
    body,
  }))
}

export const getUserById = async (userId: string): Promise<null | User> => {
  return filterResponse(await APIFetch<User>(`/users/${userId}`, {
    method: 'GET',
  }))
}

export const getUserProfile = async (userIdOrUsername: string): Promise<null | UserProfile> => {
  return filterResponse(await APIFetch<UserProfile>(`/users/${userIdOrUsername}/profile`, {
    method: 'GET',
  }))
}

// Returns true if success
export const updateUser = async (userId: string, body: UserUpdate): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}`, {
    method: 'PATCH',
    body,
  }))
}

export const getSavedPrompts = async (options: {
  userId: string
  query?: {
    page?: number
    size?: number
    order_by?: APIOrderings
    interval?: APIIntervals
    enterprise_id?: number
  }
}): Promise<null | Prompt[]> => {
  let url = `/users/${options.userId}/prompts`

  if (options.query) {
    const parsedQueries = Object.entries(options.query).map(([key, value]) => [key, String(value)])
    const query = new URLSearchParams(parsedQueries)

    url += `?${query.toString()}`
  }

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

interface IGetUserFollowersOptions {
  size?: MaybeRef<string | number>
  page?: MaybeRef<string | number>
}

export const getUserFollowers = async (userId: string, options?: IGetUserFollowersOptions): Promise<null | User[]> => {
  let url = `/users/${userId}/followers`

  if (options) {
    const parsedQueries = Object.entries(options).map(([key, value]) => [key, String(unref(value))])
    const query = new URLSearchParams(parsedQueries)

    url += `?${query.toString()}`
  }

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const getUserFollowees = async (userId: string, options?: IGetUserFollowersOptions): Promise<null | User[]> => {
  let url = `/users/${userId}/followees`

  if (options) {
    const parsedQueries = Object.entries(options).map(([key, value]) => [key, String(unref(value))])
    const query = new URLSearchParams(parsedQueries)

    url += `?${query.toString()}`
  }

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const followUser = async (userId: string): Promise<null | { userId: string; followeeId: string }> => {
  return filterResponse(await APIFetch(`/users/${userId}/follows`, {
    method: 'POST',
  }))
}

export const unfollowUser = async (userId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}/follows`, {
    method: 'DELETE',
  }))
}

export const getUserPromptModes = ['created', 'saved', 'voted', 'used', 'list', 'snippets', 'library'] as const
export type GetUserPromptMode = typeof getUserPromptModes[number]
interface GetUserPromptsQuery extends ElementalQuery {
  mode: GetUserPromptMode
  type_ids: APIPromptTypeId[]
  enterprise_id?: number
}
export const getUserPrompts = async (
  userId: string,
  query: GetUserPromptsQuery = {
    mode: 'created',
    type_ids: [1, 2],
  },
): Promise<null | PromptMinified[]> => {
  let url = `/users/${userId}/prompts`

  if (query)
    url = useParseQuery(url, query)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const getUserElementals = async (
  userId: string,
  elementalPath: string,
  query: ElementalQuery,
): Promise<null | Elemental[]> => {
  let url = `/users/${userId}/${elementalPath}`

  if (query)
    url = useParseQuery(url, query)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const savePrompt = async (userId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}/prompts/${promptId}`, {
    method: 'POST',
  }))
}

export const removeSavedPrompt = async (userId: string, promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}/prompts/${promptId}`, {
    method: 'DELETE',
  }))
}

export const getFollowedTopics = async (userId: string): Promise<null | number[]> => {
  return filterResponse(await APIFetch(`/users/${userId}/topics`, {
    method: 'GET',
  }))
}

export const followTopic = async (userId: string, topicId: number): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}/topics/${topicId}`, {
    method: 'POST',
  }))
}

export const unfollowTopic = async (userId: string, topicId: number): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}/topics/${topicId}`, {
    method: 'DELETE',
  }))
}

export const updateAvatarImage = async (userId: string, avatarImage: File): Promise<boolean> => {
  const formData = new FormData()
  formData.append('file', avatarImage)
  return filterResponseBoolean(await APIFetch(`/users/${userId}/avatar`, {
    method: 'PATCH',
    body: formData,
    isFormData: true,
  }))
}

// Returns true if username is available
export const checkUsernameAvailability = async (username: string): Promise<null | boolean> => {
  const resp = filterResponse(await APIFetch<boolean>(`/usernames?username=${username}`, {
    method: 'GET',
  }))

  if (resp === null)
    throw new Error('Failed to check availability')
  else
    return resp
}

export const getUserInformation = async (userIds: string[], signal?: AbortSignal): Promise<null | UserInformation[]> => {
  return filterResponse(await APIFetch<UserInformation[]>(useParseQuery('/user-information', { user_ids: userIds.join(',') }), {
    method: 'GET',
    signal,
  }))
}

export const updateUserAsAdmin = async (userIdOrUrl: string, body: { isHidden: boolean }): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/admin/users/${userIdOrUrl}`, {
    body,
    method: 'PATCH',
  }))
}

export const getRecentElementalItems = async (
  userId: string,
  size?: number,
): Promise<null | RecentItem[]> => {
  return filterResponse(await APIFetch(useParseQuery(`/users/${userId}/recent-items`, { size: size || 5, order_dir: 'desc' }), {
    method: 'GET',
  }))
}

export const addRecentItem = async (userId: string, itemId: string, action: RecentItemActionEnum): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/users/${userId}/recent-items/${itemId}`, {
    method: 'POST',
    body: {
      action,
    },
  }))
}
